import { Flex, Toggle } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import React, { memo, useCallback, useMemo } from "react";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import {
  useGroupTypes,
  useWorkspace,
  useWorkspaceLocale,
} from "src/contexts/global/WorkspaceContext";
import getGroupFeatureVisibility from "src/util/getGroupFeatureVisibility";
import {
  useDateDiffFeatureEnabled,
  useChangelogFeatureEnabled,
} from "src/components/OrgChart/odDemo/featureFlags";
import { DiffDatePicker } from "./components/DiffDatePicker";
import ChangelogButton from "./components/ChangelogButton";

const AnimatedToggleWrapper = styled.div`
  transition: all 0.4s ease-in-out;
  opacity: 1;
  max-height: 24px; /* Adjust based on your Toggle height */
  margin-bottom: ${({ hideBottomMargin, theme }) =>
    hideBottomMargin ? "0" : themeGet("space.r")({ theme })};

  &.hidden {
    display: none;
    max-height: 0;
    margin: 0;
    margin-bottom: 0;
  }
`;

const ViewOptions = () => {
  const { state, dispatch } = useOrgChartContext();
  const { currentViewTeam } = state;
  const groupTypes = useGroupTypes();
  const groupType = groupTypes?.[currentViewTeam?.type];
  const workspace = useWorkspace();
  const locale = useWorkspaceLocale();
  const isDateDiffEnabled = useDateDiffFeatureEnabled();
  const isChangelogEnabled = useChangelogFeatureEnabled();

  const { demandLabel = "demand", supplyLabel = "supply" } =
    workspace?.config?.allocation || {};

  // Check if principles feature is enabled for the workspace
  const groupFeatureVisibility = getGroupFeatureVisibility({
    group: currentViewTeam,
    config: workspace?.config,
  });

  // Show toggle if principles feature is enabled
  const hasActivePrinciples = groupFeatureVisibility.principles;

  const toggleViewOptions = useCallback(
    (key) => {
      dispatch({
        type: ACTION_TYPES.UPDATE_VIEW_OPTIONS,
        payload: {
          ...state.viewOptions,
          [key]: !state.viewOptions[key],
        },
      });
    },
    [dispatch, state.viewOptions]
  );

  const toggles = useMemo(() => {
    const type = groupType?.isSupply ? demandLabel : supplyLabel;

    const getHandlerAndValue = (key) => ({
      onChange: () => toggleViewOptions(key),
      isChecked: state?.viewOptions?.[key],
    });

    return [
      {
        id: "org-chart-view-options-showPeople",
        label: "Show people",
        isVisible: true,
        ...getHandlerAndValue("showPeople"),
      },
      {
        id: "org-chart-view-options-groupMembersBySupplyOrDemand",
        label: `${locale === "en-US" ? "Organize" : "Organise"} members by ${type}`,
        isVisible: groupType && state?.viewOptions?.showPeople,
        ...getHandlerAndValue("groupMembersBySupplyOrDemand"),
      },
      {
        id: "org-chart-view-options-showPrinciples",
        label: "Show principles",
        isVisible: hasActivePrinciples,
        ...getHandlerAndValue("showPrinciples"),
      },
    ];
  }, [
    groupType,
    state?.viewOptions,
    toggleViewOptions,
    demandLabel,
    supplyLabel,
    hasActivePrinciples,
    locale,
  ]);

  return (
    <Flex flexDirection="column">
      {toggles.map((toggle) => {
        return (
          <AnimatedToggleWrapper
            key={toggle.id}
            className={!toggle.isVisible ? "hidden" : ""}
          >
            <Toggle
              id={toggle.id}
              label={toggle.label}
              checked={toggle.isChecked}
              onChange={toggle.onChange}
            />
          </AnimatedToggleWrapper>
        );
      })}
      {isDateDiffEnabled && (
        <DiffDatePicker state={state} dispatch={dispatch} />
      )}
      {isChangelogEnabled && (
        <ChangelogButton state={state} dispatch={dispatch} />
      )}
    </Flex>
  );
};

export default memo(ViewOptions);
